import React, { useState, useEffect } from 'react';
import { SellingAllowed, useCanCreateListings } from '../../../../context/sellingAccessContext';
import { arrayOf, bool, func, object, number, shape, string } from 'prop-types';
import classNames from 'classnames';
import { GoBell } from 'react-icons/go';
import { FaRegHeart } from 'react-icons/fa';
import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { propTypes } from '../../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  ChangeLanguageForm,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';

import css from './TopbarDesktop.module.css';
import { SHOW_EMAIL_VERIFICATION_POPUP } from '../../../../ducks/user.duck';
import { useDispatch } from 'react-redux';

const TopbarDesktop = props => {
  const canCreateListings = useCanCreateListings();
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    pathname,
    history,
    routeConfiguration,
    searchParams,
    clearKeywordsAfterSubmit,
  } = props;
  const [mounted, setMounted] = useState(false);
  const { attributes } = currentUser || {};
  const { emailVerified } = attributes || {};
  useEffect(() => {
    setMounted(true);
  }, []);
  const dispatch = useDispatch();
  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const search = (
    <TopbarSearchForm
      className={css.searchLink}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={appConfig}
      history={history}
      routeConfiguration={routeConfiguration}
      searchParams={searchParams}
      clearKeywordsAfterSubmit={clearKeywordsAfterSubmit}
    />
  );

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.inboxLink}
      name="InboxPage"
      params={{ tab: canCreateListings && currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <GoBell className={css.bell} />
        {notificationCount ? (
          <span className={css.notificationBadge}>
            {notificationCount > 0 ? notificationCount : null}
          </span>
        ) : null}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {canCreateListings && (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        )}
        <MenuItem key="InboxBaseOrder">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('InboxBaseOrder'))}
            params={{ tab: 'orders' }}
            name="InboxBaseOrder"
          >
            {' '}
            <span className={css.menuItemBorder} />
            {intl.formatMessage({ id: 'TopbarDesktop.TopbarDesktop.myOrders' })}
          </NamedLink>
        </MenuItem>
        {canCreateListings && (
          <MenuItem key="InboxBasePage">
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('InboxBasePage'))}
              params={{ tab: 'sales' }}
              name="InboxBasePage"
            >
              {' '}
              <span className={css.menuItemBorder} />
              {intl.formatMessage({ id: 'TopbarDesktop.TopbarDesktop.mySales' })}
            </NamedLink>
          </MenuItem>
        )}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        {/* name="InboxPage"
        params={{ tab: currentUserHasListings ? 'sales' : 'orders' }} */}

        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" to={{ from: pathname }} className={css.signupLink}>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const savedPropertiesLink = mounted ? (
    <NamedLink className={css.savedPropertiesLink} name="WishlistPage">
      <span className={css.savedProperties}>
        <FaRegHeart className={css.savedPropertiesIcon} />
      </span>
    </NamedLink>
  ) : null;

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" to={{ from: pathname }} className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  return (
    <nav className={classes}>
      <div className={css.main}>
        {search}
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        />
        <div className={css.internalLinks}>
          <SellingAllowed>
            {isAuthenticated && emailVerified ? (
              <NamedLink className={css.createListingLink} name="NewListingPage">
                <span className={css.createListing}>
                  <FormattedMessage id="TopbarDesktop.createListing" />
                </span>
              </NamedLink>
            ) : (
              isAuthenticated && (
                <div
                  className={css.createListingLink}
                  onClick={() => {
                    dispatch({ type: SHOW_EMAIL_VERIFICATION_POPUP, payload: true });
                  }}
                >
                  <span className={css.createListing}>
                    <FormattedMessage id="TopbarDesktop.createListing" />
                  </span>
                </div>
              )
            )}
            {isAuthenticatedOrJustHydrated ? null : (
              <NamedLink className={css.createListingLink} name="NewListingPage">
                <span className={css.createListing}>
                  <FormattedMessage id="TopbarDesktop.createListing" />
                </span>
              </NamedLink>
            )}
          </SellingAllowed>

          {savedPropertiesLink}
          {inboxLink}
          {profileMenu}

          {loginLink}
          {signupLink}

          <ChangeLanguageForm currentUser={currentUser} />
        </div>
      </div>
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
  searchParams: null,
  clearKeywordsAfterSubmit: false,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  searchParams: object,
  clearKeywordsAfterSubmit: bool,
};

export default TopbarDesktop;
